import type {
  AssetView,
  CanvasDocumentV3 as CanvasDocument,
  SeedanceCapabilitiesView
} from "@seedance/contracts";
import { canvasDocumentV3Schema } from "@seedance/contracts";
import {
  createSeedanceRequestCompilerV3,
  type BuildPreviewResult
} from "@seedance/workflow";
import { assetThumbnailUrl } from "../nodes/node-types.js";
import { canvasCopy, referenceModeLabel, referenceRoleLabel } from "../../copy/zh-CN.js";

type SeedanceNode = Extract<CanvasDocument["nodes"][number], { kind: "seedance" }>;

export interface RequestPreviewPanelProps {
  document: CanvasDocument;
  seedanceNode: SeedanceNode;
  capabilities: SeedanceCapabilitiesView;
  assets: readonly AssetView[];
  onPreview?(preview: BuildPreviewResult): void;
}

export interface EditorRequestPreviewBuilder {
  readonly document: CanvasDocument;
  resolve: ReturnType<typeof createSeedanceRequestCompilerV3>["resolve"];
  build(options: Omit<RequestPreviewPanelProps, "onPreview" | "document">): BuildPreviewResult;
}

// Only genuine strict-valid builders can provide reuse state. The key is a
// detached string, never the caller's mutable document or public facade fields.
const builderStates = new WeakMap<EditorRequestPreviewBuilder, {
  semanticKey: string;
  compiler: ReturnType<typeof createSeedanceRequestCompilerV3>;
}>();

export function createEditorRequestPreviewBuilder(document: CanvasDocument, previous?: EditorRequestPreviewBuilder): EditorRequestPreviewBuilder {
  // Geometry still participates in strict validation, including the UTF-8
  // whole-document size limit. Invalid bindings keep the compiler's existing
  // target-local diagnostics; no failed parse is eligible for reuse.
  const parsed = canvasDocumentV3Schema.safeParse(document);
  const semanticKey = parsed.success ? (() => {
    const { viewport: _viewport, nodes, ...rest } = parsed.data;
    return JSON.stringify({ ...rest, nodes: nodes.map(({ position: _position, size: _size, ...node }) => node) });
  })() : undefined;
  const prior = previous ? builderStates.get(previous) : undefined;
  const compiler = semanticKey !== undefined && prior?.semanticKey === semanticKey
    ? prior.compiler
    : createSeedanceRequestCompilerV3(parsed.success ? parsed.data : document);
  const build = ({ seedanceNode, capabilities, assets }: Omit<RequestPreviewPanelProps, "onPreview" | "document">): BuildPreviewResult => {
    const resolved = compiler.resolve(seedanceNode.id);
    const compiled = compiler.check({
      seedanceNodeId: seedanceNode.id,
      // Browser preview can only see assets already authorized for this session.
      // The API compiler repeats authorization with the authenticated owner.
      ownerId: "browser-preview",
      capabilities,
      assets: new Map(assets.map((asset) => [asset.id, {
        asset,
        ownerId: "browser-preview",
        readable: true
      }]))
    });
    if (compiled.ok) {
      return {
        ready: true,
        finalPrompt: compiled.finalPrompt,
        references: [...compiled.references],
        parameters: { ...seedanceNode.settings, prompt: resolved.finalPrompt },
        errors: [],
        warnings: compiled.references
          .filter((reference) => reference.instruction.trim().length === 0)
          .map((reference) => ({
            code: "EMPTY_REFERENCE_INSTRUCTION",
            message: `${reference.mention} 尚未填写可见的引用说明。`
          }))
      };
    }
    return {
      ready: false,
      finalPrompt: resolved.finalPrompt,
      references: [],
      parameters: { ...seedanceNode.settings, prompt: resolved.finalPrompt },
      errors: [{ code: compiled.code, message: compiled.message }],
      warnings: []
    };
  };
  const builder = Object.freeze({document, resolve: compiler.resolve, build});
  if (semanticKey !== undefined) builderStates.set(builder, { semanticKey, compiler });
  return builder;
}

export function buildEditorRequestPreview({document, ...options}: Omit<RequestPreviewPanelProps, "onPreview">): BuildPreviewResult {
  return createEditorRequestPreviewBuilder(document).build(options);
}

export function RequestPreviewPanel(props: RequestPreviewPanelProps) {
  const preview = buildEditorRequestPreview(props);
  const assetsById = new Map(props.assets.map((asset) => [asset.id, asset]));
  return (
    <section className="request-preview" aria-label={canvasCopy.requestPreview}>
      <header>
        <h3>请求预览</h3>
        <span className={preview.ready ? "request-status request-status--ready" : "request-status request-status--error"}>
          {preview.ready ? canvasCopy.ready : canvasCopy.error}
        </span>
      </header>
      <div className="request-preview__prompt">
        <strong>最终提示词</strong>
        <pre>{preview.finalPrompt || "（空）"}</pre>
      </div>
      <ol className="request-preview__references">
        {preview.references.map((reference) => {
          const asset = assetsById.get(reference.assetId);
          return (
            <li key={reference.edgeId}>
              {reference.kind === "image" || reference.kind === "video" ? (
                <img src={assetThumbnailUrl(reference.assetId)} alt={asset?.displayName ?? reference.mention} />
              ) : (
                <span className="request-preview__audio" aria-hidden="true">∿</span>
              )}
              <div>
                <strong>{reference.mention}</strong>
                <span>{asset?.displayName ?? reference.assetId}</span>
                <small>{canvasCopy.role}={referenceRoleLabel(reference.role)} · {canvasCopy.mode}={reference.mode ? referenceModeLabel(reference.mode) : "—"} · {canvasCopy.order}={reference.order}</small>
                <p>{reference.instruction || "（无说明）"}</p>
              </div>
            </li>
          );
        })}
      </ol>
      <dl className="request-preview__parameters">
        {Object.entries(preview.parameters).map(([key, value]) => (
          <div key={key}><dt>{key}</dt><dd>{String(value)}</dd></div>
        ))}
      </dl>
      {preview.errors.length > 0 ? (
        <ul className="request-preview__messages request-preview__messages--error">
          {preview.errors.map((error, index) => <li key={`${error.code}-${index}`}>{error.code}: {error.message}</li>)}
        </ul>
      ) : null}
      {preview.warnings.length > 0 ? (
        <ul className="request-preview__messages request-preview__messages--warning">
          {preview.warnings.map((warning, index) => <li key={`${warning.code}-${index}`}>{warning.code}: {warning.message}</li>)}
        </ul>
      ) : null}
      <button
        className="foundation-button foundation-button--ghost"
        type="button"
        onClick={() => props.onPreview?.(buildEditorRequestPreview(props))}
      >检查请求（不提交 4399）</button>
    </section>
  );
}
